import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePopper } from "react-popper";

import { MachineSubmenuSizeButton } from "@/sx-layout/components/plotmap/components/MachineSubmenuSizeButton";
import { useChangeMarkSize } from "@/sx-layout/components/plotmap/hooks/useChangeMarkSize";
import { Mark } from "@/sx-layout/models";

type Props = {
  referenceElement: HTMLElement;
  mark: Mark;
  scale: number;
  control: {
    isPast: boolean;
    isToday: boolean;
    isAdmin: boolean;
    isUser: boolean;
    isMarkFromOtherCompany: boolean;
  };
  onEdit: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDuplicate: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDelete: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onChangeSize: (m: Mark) => void;
};

export const MarkSubMenu: FC<Props> = ({
  referenceElement,
  mark,
  scale,
  control,
  onEdit,
  onDuplicate,
  onDelete,
  onChangeSize,
}) => {
  const { t } = useTranslation();

  const [popperElement, setPopperElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "right",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 20],
        },
      },
    ],
  });

  const position = attributes?.popper?.["data-popper-placement"];

  const markSize = useChangeMarkSize(mark, onChangeSize);

  // 連打防止用
  const [changingStatus, setChangingStatus] = useState(false);
  const throttle = (callback: () => void) => {
    if (changingStatus) return;
    setChangingStatus(true);
    callback();
    setTimeout(() => setChangingStatus(false), 1000);
  };

  const isUserAndOwnCompany = control.isUser && !control.isMarkFromOtherCompany;
  if (!(control.isAdmin || isUserAndOwnCompany)) {
    return <></>;
  }

  return (
    referenceElement && (
      <div
        id="layout-target-submenu"
        className="z-10"
        style={{
          ...styles.popper,
          transform: `${styles.popper?.transform ?? ""} scale(${scale})`,
          transformOrigin:
            position === "left" ? "right" : position === "top" ? "bottom" : position === "bottom" ? "top" : "left",
        }}
        ref={setPopperElement}
        {...attributes.popper}
      >
        <div id="layout-target-submenu-arrow" data-popper-arrow />
        <div className="bg-[#f3f2ef] p-[5px] w-[140px] font-bold rounded-[5px]">
          <ul id={`mark-submenu-${mark.mark_id}`} className="bg-[#f3f2ef]">
            {/* 編集 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li
                onClick={(e) => throttle(() => onEdit(e))}
                className="bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px] mb-[5px]"
              >
                {t("edit")}
              </li>
            ) : null}
            {/* 後日複製 */}
            {control.isAdmin || isUserAndOwnCompany ? (
              <li
                onClick={(e) => throttle(() => onDuplicate(e))}
                className={
                  `bg-[#fff] hover:bg-[#fff8ee] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px]` +
                  (control.isPast ? "" : " mb-[5px]")
                }
              >
                {t("duplicate")}
              </li>
            ) : null}
            {/* サイズ変更 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li className="cursor-pointer rounded flex items-center justify-between p-[2px] text-[13px] mb-[5px]">
                <MachineSubmenuSizeButton onClick={markSize.decrease} disabled={markSize.size <= 0.5}>
                  <img src="./img/icon-minus-black.svg" className="w-[16px] h-[16px]" />
                </MachineSubmenuSizeButton>
                <div className="leading-[1.2rem] text-center">
                  {t("size")}
                  <br />
                  <span className="text-[1rem]">{Math.round(markSize.size * 100)}%</span>
                </div>
                <MachineSubmenuSizeButton onClick={markSize.increase} disabled={2 <= markSize.size}>
                  <img src="./img/icon-plus-black.svg" className="w-[16px] h-[16px]" />
                </MachineSubmenuSizeButton>
              </li>
            ) : null}
            {/* 削除 */}
            {!control.isPast && (control.isAdmin || isUserAndOwnCompany) ? (
              <li
                onClick={(e) => throttle(() => onDelete(e))}
                className="bg-[#ffdddd] hover:bg-[#f8d0d0] cursor-pointer rounded flex items-center justify-center p-[5px] text-[13px]"
              >
                {t("delete")}
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    )
  );
};
